/*
 * The client's destinations, moved here from AppShell.tsx in build round 41 (2026-10-11) so the
 * client's shell and "Visa som kund" share one list and one set of rules.
 */
import { ChartBar, ChatCircleText, Files, GearSix, GridFour, IdentificationCard, Key, ListChecks, Storefront } from "@phosphor-icons/react";

export const DESTINATIONS = [
  { to: "/overview", label: "Översikt", icon: GridFour, primary: true },
  { to: "/chat", label: "Chatt", icon: ChatCircleText, primary: true },
  { to: "/requests", label: "Ärenden", icon: ListChecks, primary: true },
  { to: "/documents", label: "Dokument", icon: Files, primary: true },
  { to: "/accounts", label: "Konton", icon: IdentificationCard, primary: false },
  { to: "/settings", label: "Inställningar", icon: GearSix, primary: false }
] as const;

/**
 * The six destinations `design.md` section 5 fixes since 2026-09-26, in the order it fixes them.
 *
 * Analys och drift was the seventh until build round 4 (2026-09-27). Mattej removed it with the
 * sketch he approved on 2026-09-26: every figure it held and the website's status moved to Översikt,
 * and a destination with nothing left on it is a dead click. Dokument took its place on the phone's
 * bar, because the reason Analys och drift was there, that an outage notice landed on it, went with
 * it. `tests/portal-interface.test.tsx` guards the list.
 *
 * The rule the list has followed since 2026-09-02 still holds: a destination is never hidden while it
 * is unfinished. What is wrong is fixed at the destination, and a destination that has nothing left
 * to hold is removed, as this one was, rather than hidden.
 */
export const MOBILE_PRIMARY = DESTINATIONS.filter((item) => item.primary);

/**
 * The Mer sheet on a phone: the two destinations the bottom bar has no room for, and Hemligheter.
 *
 * Hemligheter is not one of the six and does not become one. It is added here, and to the company's
 * group in Inställningar, because until the second build round (2026-09-24) the only way in was a
 * line under the chat composer, and a secret Petrovic sends a client arrives with no link of its own
 * but the notification. Approved with the client sketch (`prototype/kund/pages.js`, `SHEETS.mer`).
 */
export const MOBILE_SECONDARY = [
  ...DESTINATIONS.filter((item) => !item.primary),
  { to: "/secrets", label: "Hemligheter", icon: Key, primary: false }
] as const;

/**
 * Webbplatsens fakta, build round 19 (2026-10-08), decided by the planning chat with the round's
 * contract: in the rail after Dokument and before Konton, and first in the Mer sheet on a phone.
 *
 * It is not a seventh destination of design.md section 5 and is not offered to every client. It is
 * shown only when `client_site_facts_sites` answered, successfully, with at least one website that
 * has facts: hidden while that read is on its way and when it failed, never guessed. A client with no
 * such website has nothing to do there, and a place that opens on "nothing here" for most clients is
 * the dead click the rule of 2026-09-26 removed Analys och drift for. A destination is still never
 * hidden while it is unfinished; this one is hidden while it has nothing to hold.
 */
export const FACTS_DESTINATION = {
  to: "/site-facts",
  label: "Webbplatsens fakta",
  icon: Storefront,
  primary: false
} as const;

export function withFacts<T extends { to: string }>(items: readonly T[], after: string | null): Array<T | typeof FACTS_DESTINATION> {
  if (after === null) {
    return [FACTS_DESTINATION, ...items];
  }
  const at = items.findIndex((item) => item.to === after) + 1;
  return [...items.slice(0, at), FACTS_DESTINATION, ...items.slice(at)];
}

/**
 * Statistik, build round 37 (2026-10-11), decided by Mattej via Portal HQ: in the navigation only for
 * a client with a website under management, by the same rule as Webbplatsens fakta. Shown when
 * `client_site_counts` answered, successfully, with at least one website, which it does for every
 * website under management whether or not anything is measured yet. After Webbplatsens fakta when that
 * is shown, so the facts keep their place; otherwise after Dokument in the rail and first in Mer.
 */
export const STATISTICS_DESTINATION = {
  to: "/statistics",
  label: "Statistik",
  icon: ChartBar,
  primary: false
} as const;

export function withStatistics<T extends { to: string }>(
  items: readonly T[],
  after: string | null
): Array<T | typeof STATISTICS_DESTINATION> {
  if (after === null) {
    return [STATISTICS_DESTINATION, ...items];
  }
  const at = items.findIndex((item) => item.to === after) + 1;
  return [...items.slice(0, at), STATISTICS_DESTINATION, ...items.slice(at)];
}
